import { hFetch } from '@/lib/hfetch';

export type InAppPhoneCallEntity = {
  id: number;
  webrtc_call_id: string;
  voice_api_call_id: string;
  direction: 'inbound' | 'outbound';
  call_status: 'queued' | 'ringing' | 'in_progress' | 'completed' | 'failed' | 'busy' | 'canceled';
  started_at: string;
  ended_at?: string;
  call_duration?: number;
  duration_in_minutes?: number;
  recording_url?: string;
  transcript?: string;
  transcript_generated_at?: string;
  created_at: string;
  updated_at: string;
  user_caller?: {
    id: number;
    name: string;
    email: string;
  };
};

export const getRecordingUrl = async (id: number): Promise<string> => {
  const response = await hFetch<{ recording_url: string }>({
    url: `/api/in_app_phone_calls/${id}/recording`,
  });

  if (response.status === 'error') {
    throw new Error(response.messages.join(', '));
  }

  return response.data.recording_url;
};

export const getTelnyxToken = async (): Promise<string> => {
  const response = await hFetch<{ token: string }>({
    url: '/api/telnyx_tokens',
    method: 'POST',
  });

  if (response.status === 'error') {
    throw new Error(response.messages.join(', '));
  }

  return response.data.token;
};

export type UpdateInAppPhoneCallAttributes = {
  call_status?: InAppPhoneCallEntity['call_status'];
  ended_at?: string;
  call_duration?: number;
};

export const inAppPhoneCallsApi = {
  async getForContact(contactId: number): Promise<InAppPhoneCallEntity[]> {
    const response = await hFetch<InAppPhoneCallEntity[]>({
      url: `/api/in_app_phone_calls?contact_id=${contactId}`,
    });
    if (response.status === 'error') throw new Error(response.messages.join(', '));
    return response.data;
  },

  async update(id: number, attrs: UpdateInAppPhoneCallAttributes): Promise<InAppPhoneCallEntity> {
    const response = await hFetch<InAppPhoneCallEntity, { in_app_phone_call: UpdateInAppPhoneCallAttributes }>({
      url: `/api/in_app_phone_calls/${id}`,
      method: 'PATCH',
      payload: { in_app_phone_call: attrs },
    });
    if (response.status === 'error') throw new Error(response.messages.join(', '));
    return response.data;
  },

  async getLatestWithRecordingForContact(contactId: number): Promise<InAppPhoneCallEntity | null> {
    const calls = await this.getForContact(contactId);
    const withRecording = calls.filter(c => !!c.recording_url);
    if (withRecording.length === 0) return null;
    return withRecording.sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())[0];
  },

  getRecordingUrl,
};
